import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';

import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { format } from 'date-fns';

const ContactNotesPanel = ({ contact, userId, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddingNote, setIsAddingNote] = useState(false);

  useEffect(() => {
    if (contact?.id) {
      loadContactNotes();
    }
  }, [contact?.id]);

  const loadContactNotes = async () => {
    if (!contact?.id || !userId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        ?.from('call_logs')
        ?.select('*')
        ?.eq('contact_id', contact?.id)
        ?.eq('user_id', userId)
        ?.not('call_notes', 'is', null)
        ?.order('created_at', { ascending: false });

      if (fetchError) {
        setError('Failed to load contact notes');
        return;
      }

      setNotes(data || []);
    } catch (err) {
      setError('Failed to load contact notes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote?.trim() || !contact?.id || !userId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: createError } = await supabase
        ?.from('call_logs')
        ?.insert({
          user_id: userId,
          contact_id: contact?.id,
          call_notes: newNote?.trim(),
          direction: 'outbound',
          call_started_at: new Date()?.toISOString(),
          call_ended_at: new Date()?.toISOString(),
          duration_seconds: 0,
          outcome: null
        })
        ?.select()
        ?.single();

      if (createError) {
        setError('Failed to save note');
        return;
      }

      setNotes(prev => [data, ...prev]);
      setNewNote('');
      setIsAddingNote(false);
    } catch (err) {
      setError('Failed to save note');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!noteId || !confirm('Are you sure you want to delete this note?')) return;

    setLoading(true);
    
    try {
      const { error: deleteError } = await supabase
        ?.from('call_logs')
        ?.delete()
        ?.eq('id', noteId);

      if (deleteError) {
        setError('Failed to delete note');
        return;
      }

      setNotes(prev => prev?.filter(note => note?.id !== noteId));
    } catch (err) {
      setError('Failed to delete note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="User" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">{contact?.name}</h3>
            <p className="text-sm text-muted-foreground">{contact?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          iconName="X"
        />
      </div>

      {error && (
        <div className="mb-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={14} className="text-destructive" />
            <span className="text-destructive text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Add Note Section */}
      <div className="mb-4">
        {!isAddingNote ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAddingNote(true)}
            iconName="Plus"
            iconPosition="left"
            className="w-full"
          >
            Add Note
          </Button>
        ) : (
          <div className="space-y-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e?.target?.value)}
              placeholder="Enter your note about this contact..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
            />
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={!newNote?.trim() || loading}
                iconName={loading ? "Loader2" : "Save"}
                iconPosition="left"
                className={loading ? "animate-spin" : ""}
              >
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAddingNote(false);
                  setNewNote('');
                }}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Notes List */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Icon name="FileText" size={16} className="text-muted-foreground" />
          <h4 className="font-medium text-foreground">Notes & Call Logs</h4>
        </div>

        {loading && notes?.length === 0 ? (
          <div className="text-center py-4">
            <Icon name="Loader2" size={16} className="animate-spin mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading notes...</p>
          </div>
        ) : notes?.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Icon name="FileText" size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No notes yet for this contact</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {notes?.map(note => (
              <div key={note?.id} className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={note?.duration_seconds > 0 ? 'Phone' : 'MessageSquare'} 
                      size={14} 
                      className="text-primary mt-0.5" 
                    />
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(note?.created_at), 'MMM dd, yyyy HH:mm')}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteNote(note?.id)}
                    disabled={loading}
                    iconName="Trash2"
                    className="text-destructive hover:text-destructive h-6 w-6 p-0"
                  />
                </div>
                
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {note?.call_notes}
                </p>

                {note?.duration_seconds > 0 && (
                  <div className="mt-2 flex items-center space-x-2 text-xs text-muted-foreground">
                    <Icon name="Clock" size={12} />
                    <span>Duration: {Math.floor(note?.duration_seconds / 60)}m {note?.duration_seconds % 60}s</span>
                    {note?.outcome && (
                      <>
                        <span>•</span>
                        <span className="capitalize">{note?.outcome?.replace('_', ' ')}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Details */}
      <div className="mt-4 pt-4 border-t border-border">
        <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
          <Icon name="Info" size={16} />
          <span>Contact Details</span>
        </h4>
        <div className="space-y-2 text-sm">
          {contact?.company && (
            <div className="flex items-center space-x-2">
              <Icon name="Building" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{contact?.company}</span>
            </div>
          )}
          {contact?.phone && (
            <div className="flex items-center space-x-2">
              <Icon name="Phone" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{contact?.phone}</span>
            </div>
          )}
          {contact?.email && (
            <div className="flex items-center space-x-2">
              <Icon name="Mail" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{contact?.email}</span>
            </div>
          )}
          {contact?.tags?.length > 0 && (
            <div className="flex items-center space-x-2">
              <Icon name="Tag" size={14} className="text-muted-foreground" />
              <div className="flex flex-wrap gap-1">
                {contact?.tags?.map((tag, index) => (
                  <span key={index} className="bg-primary/10 text-primary text-xs px-2 py-1 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactNotesPanel;